import { Box, TextField } from '@mui/material';
import { ChangeEvent, ClipboardEvent, KeyboardEvent, useRef } from 'react';

type PinInputProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  //  Mask digits (sign challenge) vs. show them (setup/reset, per wireframe).
  mask?: boolean;
};

//  Strip non-digits and cap to `length` (used on paste and to normalize input).
export function sanitizePin(raw: string, length: number): string {
  return raw.replace(/\D/g, '').slice(0, length);
}

//  Compute the next full PIN value when `raw` is typed into box `index`.
//  Keeps only the last digit of `raw`; a non-numeric entry clears the box.
export function applyPinDigit(current: string, index: number, raw: string, length: number): string {
  const digits = Array.from({ length }, (_, i) => current[i] ?? '');
  const cleaned = raw.replace(/\D/g, '');
  digits[index] = cleaned ? cleaned[cleaned.length - 1] : '';
  return digits.join('').slice(0, length);
}

//  THIN-7681: N-box numeric PIN entry (default 4). No OTP component existed in the app.
export default function PinInput({
  value,
  onChange,
  length = 4,
  disabled,
  error,
  autoFocus,
  mask,
}: PinInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const handleChange = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    onChange(applyPinDigit(value, i, e.target.value, length));
    if (cleaned && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = sanitizePin(e.clipboardData.getData('text'), length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {digits.map((digit, i) => (
        <TextField
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={digit}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          disabled={disabled}
          error={error}
          autoFocus={autoFocus && i === 0}
          type={mask ? 'password' : 'text'}
          inputProps={{
            inputMode: 'numeric',
            maxLength: 1,
            'aria-label': `PIN digit ${i + 1}`,
            style: { textAlign: 'center', fontSize: 22, fontWeight: 600, padding: '10px 0', width: 40 },
          }}
        />
      ))}
    </Box>
  );
}
